import { isEmpty, round } from 'lodash';

import {
   FinancialSettlementAdvancePayment,
   PaymentMethodParameters as PaymentMethodParametersApi,
   PaymentMethodType as PaymentMethodTypeApi,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { assertUnreachable } from '@dreryk/react-common';

import { VisitMedicalServiceTableBean } from '../../medicalServices/medicalServices.types';
import { getMedicalServicePrices } from '../../medicalServices/medicalServices.utils';
import { calculateTotalPrice } from '../../totalPrice/totalPrice.utils';
import { calculateAdvancePaymentsAmount } from '../advancePayment/advancePayment.utils';

import { visitSettlementPaymentStorage } from './payment.storage';
import {
   PaymentAmount,
   PaymentFormBean,
   PaymentMethod,
   PaymentMethodDescription,
   PaymentRequired,
} from './payment.types';

type CreatePaymentFormBeanOptions = {
   medicalServices: VisitMedicalServiceTableBean[];
   previousPaymentAmount?: PaymentAmount;
   previousPaymentMethod?: PaymentMethod;
   previousPaymentMethodDescription?: PaymentMethodDescription;
   advancePayments?: FinancialSettlementAdvancePayment[];
   required: PaymentRequired;
};

const assertInvalidPaymentMethodType = (paymentMethodType: Extract<PaymentMethodTypeApi, 'ONLINE_PAYMENT'>): never => {
   throw new Error(`Invalid payment method: "${paymentMethodType}"`);
};

export const createPaymentFormBean = (options: CreatePaymentFormBeanOptions): PaymentFormBean => {
   const enabledPreference = visitSettlementPaymentStorage.getEnabledPreference();
   const medicalServicePrices = getMedicalServicePrices(options.medicalServices);
   const calculatedAdvancePaymentAmount = calculateAdvancePaymentsAmount(options.advancePayments);
   const calculatedTotalPrice = round(calculateTotalPrice(...medicalServicePrices) - calculatedAdvancePaymentAmount, 2);
   const paymentAmount = calculatedTotalPrice > 0 ? calculatedTotalPrice : 0;

   return {
      required: options.required,
      enabled: options.required || enabledPreference,
      method: options.previousPaymentMethod ?? 'paymentCard',
      methodDescription: options.previousPaymentMethodDescription ?? undefined,
      amount: options.previousPaymentAmount ?? (isEmpty(medicalServicePrices) ? 1 : paymentAmount),
   };
};
export const paymentAmountToMoney = (paymentAmount: PaymentAmount): Money => Money.of(paymentAmount);

export const mapPaymentMethodToApi = (paymentMethod: PaymentMethod): PaymentMethodParametersApi => {
   switch (paymentMethod) {
      case 'cash':
         return 'CASH';
      case 'paymentCard':
         return 'PAYMENT_CARD';
      case 'bankTransfer':
         return 'BANK_TRANSFER';
      case 'other':
         return 'OTHER';
      default:
         return assertUnreachable(paymentMethod);
   }
};

export const mapPaymentMethodApi = (paymentMethodType: PaymentMethodTypeApi): PaymentMethod => {
   switch (paymentMethodType) {
      case 'CASH':
         return 'cash';
      case 'PAYMENT_CARD':
         return 'paymentCard';
      case 'BANK_TRANSFER':
         return 'bankTransfer';
      case 'OTHER':
         return 'other';
      default:
         return assertInvalidPaymentMethodType(paymentMethodType);
   }
};

const MIN_VALUES = { ZERO: 0, GREATER_THAN_ZERO: 0.01 };

export const getMinValueForPaymentAmount = (amountDue: number, advancePaymentsAmount: number): number => {
   return advancePaymentsAmount !== 0 && amountDue <= 0 ? MIN_VALUES.ZERO : MIN_VALUES.GREATER_THAN_ZERO;
};
